import useMusioApi from "@/contexts/client/musio-client-context";
import { useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "wouter";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox";
import { LoadingIndicator } from "@/components/ui/loading-indicator";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const TOKEN_QUERY_PARAM = "token";

// verify token via get endpoint
// check if anything is required for the user to input
// if not then submit token
// else present required form and then submit
//
// need email from GET endpoint response

export default function VerifyPage() {
  const { musioApi } = useMusioApi();
  const [_, navigate] = useLocation();
  const [params] = useSearchParams();
  const token = params.get(TOKEN_QUERY_PARAM);

  if (!token) {
    navigate("~/error");
    return;
  }

  const [required, setRequired] = useState<string[] | null>(null);
  const [error, setError] = useState("");

  const [contactName, setContactName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreeBox, setAgreeBox] = useState(false);

  const emailRef = useRef<string>(undefined);
  const passwordRef = useRef<HTMLInputElement>(null);
  const contactNameRef = useRef<HTMLInputElement>(null);

  const contactNameRequired = !!required?.includes("contactName");
  const passwordRequired = !!required?.includes("password");

  const didAutoMutateRef = useRef(false);

  const { mutate, isPending: isMutationPending, isSuccess: isVerified } = musioApi.useMutation("post", "/verify", {
    onError: () => {
      setError("Something went wrong. Please try again.");
    },
    onSuccess: () => {
      if (window.zaraz) {
        const email = emailRef.current;
        zaraz.set('email', email);
        zaraz.track("AccountVerified");
      }
    },
  });

  const { data, isError, isPending: isVerifyPending } = musioApi.useQuery("get", "/verify", {
    params: { query: { token: token ?? undefined } },
    enabled: Boolean(token),
  });

  useEffect(() => {
    if (isError) {
      navigate("~/error");
    }
  }, [isError, navigate]);

  useEffect(() => {
    if (!data || isVerifyPending) return;

    if (Array.isArray(data.required)) {
      setRequired(data.required);
    } else if (!didAutoMutateRef.current) {
      didAutoMutateRef.current = true;
      mutate({
        body: { token },
      });
    }
  }, [data, isVerifyPending, mutate, token]);

  const validate = (): string | null => {
    if (!agreeBox) return "Must agree to Terms of Service";
    if (passwordRequired && password !== confirmPassword) return "Passwords must match";
    if (contactNameRequired && !contactName.trim()) return "Contact name is required";
    return null;
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    setError("");

    const err = validate();
    if (err) {
      setError(err);
      return;
    }

    mutate({
      body: {
        token,
        contactName: contactNameRequired ? contactName : undefined,
        password: passwordRequired ? password : undefined,
      },
    });
  };

  const isPending = isVerifyPending || isMutationPending;

  if (!token) return null;

  if (isPending) {
    return (
      <div className="full centered">
        <div className="musio-glass p-7 centered">
          <LoadingIndicator />
        </div>
      </div>
    );
  }

  if (required && required.length > 0) {
    return (
      <div className="full centered">
        <div className="centered">
          <Card className="flex flex-col items-center justify-center musio-glass w-full max-w-[478px] p-4 space-y-6">
            <CardHeader className="w-full text-center">
              <CardTitle className="font-semibold text-3xl font-inter">
                Please confirm the following
              </CardTitle>
            </CardHeader>

            <form onSubmit={handleSubmit} className="w-full">
              <CardContent className="w-full space-y-6">
                <div className="space-y-2 w-full">
                  {contactNameRequired && (
                    <Input
                      ref={contactNameRef}
                      type="text"
                      id="contactName"
                      placeholder="Name."
                      value={contactName}
                      onChange={(e) => setContactName(e.target.value)}
                      className="bg-musio-bg-light border-musio-bg-dark"
                    />
                  )}
                  {passwordRequired && (
                    <>
                      <Input
                        ref={passwordRef}
                        type="password"
                        id="password"
                        placeholder="Password."
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="bg-musio-bg-light border-musio-bg-dark"
                      />
                      <Input
                        type="password"
                        id="confirmPassword"
                        placeholder="Confirm Password."
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="bg-musio-bg-light border-musio-bg-dark"
                      />
                    </>
                  )}
                </div>

                <div className="space-y-6">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="terms-of-service"
                      checked={agreeBox}
                      onCheckedChange={(checked) => setAgreeBox(Boolean(checked))}
                      className="cursor-pointer"
                    />
                    <label
                      htmlFor="terms-of-service"
                      className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      style={{ color: "var(--musio-text-light)", opacity: "50%" }}
                    >
                      I agree with the Terms of Service & Privacy Policy
                    </label>
                  </div>
                </div>
              </CardContent>

              <CardFooter className="w-full">
                <div className="flex flex-col gap-2 w-full">
                  {error && (
                    <div className="break-words text-red-600 whitespace-pre-wrap w-full">
                      {error}
                    </div>
                  )}
                  <Button
                    type="submit"
                    className="w-full border-primary bg-musio-green font-bold text-musio-mid-gray border-none"
                    variant="outline"
                    disabled={isPending}
                  >
                    {isPending ? <LoadingIndicator /> : "Submit"}
                  </Button>
                </div>
              </CardFooter>
            </form>
          </Card>
        </div>
      </div>
    );
  }

  if (isVerified || (!required && !isPending)) {
    return (
      <div className="full centered">
        <div className="centered">
          <Card className="flex flex-col items-center justify-center musio-glass w-full max-w-[478px] p-4 space-y-6">
            <CardHeader className="w-full text-center">
              <CardTitle className="font-semibold text-3xl font-inter">
                Your Account is Verified!
              </CardTitle>
            </CardHeader>
            <CardFooter className="w-full">
              <Button
                className="w-full border-primary bg-musio-green font-bold text-musio-mid-gray border-none"
                variant="outline"
                onClick={() => navigate("~/account")}
              >
                Log In
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="full centered">
      <div className="musio-glass p-7 centered">
        <LoadingIndicator />
      </div>
    </div>
  );
}
